/* eslint-disable @typescript-eslint/naming-convention */
import React, { useCallback, useEffect, useMemo } from "react";
import { IRemovePlanMessage, RenderingDetails } from "@swiggy-private/cart-types";

import { headerBuilder } from "./helpers/bottomsheetNodeBuilder";

import { PlanPurchaseConfirmationProps } from "@swiggy-private/dls-web-recipe-common-web/dist/components/SwiggyOne/PlanRemoveConfirmationBottomsheet";
import { SwiggyOneBottomsheetHandlers } from "./types";
import { BOTTOMSHEET_BLCK_CTAS_TEXTS, BOTTOMSHEET_CTAS_TEXTS } from "./constants";
import { PLACEHOLDERS } from "@super-ui/constants";
import { isSwiggyBlack } from "./helpers";

// Components
import { RemoveHalfcardTitle } from "@super-ui/components/cart-upsell-nudge/Components/RemoveHalfcardTitle";
import { isEmpty } from "@swiggy-private/js-utils";
import { getCTA } from "@super-ui/helpers/general";

const useGetPropsForPlanConfirmationBS = (
    itemData: RenderingDetails,
    showRemovePlanConfirmation: boolean,
    handlers: SwiggyOneBottomsheetHandlers,
) => {
    const { meta = {} } = itemData;
    const { remove_plan_message = {}, plan_details } = meta;
    const removePlanMessage: IRemovePlanMessage = remove_plan_message;

    const isSwiggyBlackType = useMemo(() => isSwiggyBlack(plan_details), [plan_details]);

    // icon
    const icon = useMemo(() => remove_plan_message?.logo_ctx?.logo || "", [remove_plan_message?.logo_ctx?.logo]);

    // Title
    const titleText = useMemo(() => remove_plan_message?.title?.text || "", [remove_plan_message?.title?.text]);
    const titleUnicode = useMemo(() => remove_plan_message?.title?.attribute_values?.unicode || "", [
        remove_plan_message?.title?.attribute_values?.unicode,
    ]);
    // Content details
    const contentDetails = useMemo(() => {
        const content = remove_plan_message?.content?.[0] || {};
        const subTitle = content.sub_title || "";

        return {
            contentTitle: content?.title || "",
            subTitle,
            contentTitleNode: <RemoveHalfcardTitle content={content} isSwiggyBlackType={isSwiggyBlackType} />,
        };
    }, [isSwiggyBlackType, remove_plan_message?.content]);

    const subTitle = useMemo(() => remove_plan_message?.content?.[0]?.sub_title || "", [remove_plan_message?.content]);
    const savingsValue = useMemo(() => remove_plan_message?.content?.[0]?.attribute_values?.svg_value || "", [
        remove_plan_message?.content,
    ]);

    const {
        removeSuper,
        setShowRemovePlanConfirmation,
        impressionSuperRemovePlanConfirmationHalfCard,
        impressionSuperRemovePlanHalfCardRemoveButton,
        impressionSuperRemovePlanHalfCardBackButton,
        onClickSuperRemovePlanHalfCardBackButton,
        onClickSuperRemovePlanHalfCardRemoveButton,
    } = handlers;

    const messageInAnalyticsEvents = useMemo(() => {
        return contentDetails.contentTitle.replace(PLACEHOLDERS.BS_TITLE_SVG_VAL, savingsValue);
    }, [contentDetails.contentTitle, savingsValue]);

    const headerTitle = useMemo(() => headerBuilder(titleText, titleUnicode), [titleText, titleUnicode]);

    const BottomsheetCtaTexts = useMemo(
        () => (isSwiggyBlackType ? BOTTOMSHEET_BLCK_CTAS_TEXTS : BOTTOMSHEET_CTAS_TEXTS),
        [isSwiggyBlackType],
    );

    const hasSignificantCartDifference =
        !isEmpty(removePlanMessage) &&
        !isEmpty(removePlanMessage.attribute_values) &&
        Number(removePlanMessage.attribute_values.plan_in_cart_savings) >
            Number(removePlanMessage.attribute_values.cart_total_diff_config);

    const { primaryCTA, secondaryCTA } = getCTA({
        removePlanMessage,
        hasSignificantCartDifference,
        planType: plan_details?.type,
        bottomsheetCtaTexts: BottomsheetCtaTexts,
    });

    useEffect(() => {
        if (showRemovePlanConfirmation) {
            // whenever bottomsheet is mounted fire these impressions
            impressionSuperRemovePlanConfirmationHalfCard({
                savingsValue,
                title: titleText,
                message: messageInAnalyticsEvents,
                subMessage: subTitle,
                planId: plan_details?.plan_id,
                variant: removePlanMessage?.variant || "-",
                initialBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_not_added || "-",
                finalBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_added || "-",
            });
            impressionSuperRemovePlanHalfCardBackButton({
                planName: plan_details?.name || "-",
                planId: plan_details?.plan_id || "-",
                cta: primaryCTA,
                variant: removePlanMessage?.variant || "-",
                initialBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_not_added || "-",
                finalBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_added || "-",
            });
            impressionSuperRemovePlanHalfCardRemoveButton({
                planName: plan_details?.name || "-",
                planId: plan_details?.plan_id || "-",
                cta: secondaryCTA,
                variant: removePlanMessage?.variant || "-",
                initialBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_not_added || "-",
                finalBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_added || "-",
            });
        }
    }, [
        removePlanMessage,
        primaryCTA,
        secondaryCTA,
        impressionSuperRemovePlanConfirmationHalfCard,
        impressionSuperRemovePlanHalfCardBackButton,
        impressionSuperRemovePlanHalfCardRemoveButton,
        messageInAnalyticsEvents,
        plan_details?.name,
        plan_details?.plan_id,
        savingsValue,
        showRemovePlanConfirmation,
        subTitle,
        titleText,
    ]);

    const planRemovalConfirmationPrimaryCTA = useCallback(() => {
        setShowRemovePlanConfirmation(false);
        onClickSuperRemovePlanHalfCardBackButton({
            planName: plan_details?.name || "-",
            planId: plan_details?.plan_id || "-",
            cta: primaryCTA,
            variant: removePlanMessage?.variant || "-",
            initialBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_not_added || "-",
            finalBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_added || "-",
        });
    }, [
        primaryCTA,
        onClickSuperRemovePlanHalfCardBackButton,
        plan_details?.name,
        plan_details?.plan_id,
        setShowRemovePlanConfirmation,
        removePlanMessage?.variant,
        removePlanMessage?.attribute_values?.cart_total_plan_not_added,
        removePlanMessage?.attribute_values?.cart_total_plan_added,
    ]);

    const planRemovalConfirmationSecondaryCTA = useCallback(() => {
        setShowRemovePlanConfirmation(false);
        onClickSuperRemovePlanHalfCardRemoveButton({
            planName: plan_details?.name || "-",
            planId: plan_details?.plan_id || "-",
            cta: secondaryCTA,
            variant: removePlanMessage?.variant || "-",
            initialBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_not_added || "-",
            finalBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_added || "-",
        });
        removeSuper();
    }, [
        secondaryCTA,
        onClickSuperRemovePlanHalfCardRemoveButton,
        plan_details?.name,
        plan_details?.plan_id,
        removeSuper,
        setShowRemovePlanConfirmation,
        removePlanMessage?.variant,
        removePlanMessage?.attribute_values?.cart_total_plan_not_added,
        removePlanMessage?.attribute_values?.cart_total_plan_added,
    ]);

    return {
        bottomSheetProps: {
            icon: icon,
            title: {
                text: headerTitle,
            },
            content: [
                {
                    title: contentDetails.contentTitleNode,
                    subTitle: subTitle,
                },
            ],
            ctas: {
                primaryCTA: {
                    text: BottomsheetCtaTexts.PRIMARY,
                    handler: planRemovalConfirmationPrimaryCTA,
                },
                secondaryCTA: {
                    text: BottomsheetCtaTexts.SECONDARY,
                    handler: planRemovalConfirmationSecondaryCTA,
                },
            },
            closeOnOverlayClick: false,
        } as PlanPurchaseConfirmationProps,
        removePlanMessage,
    };
};

export default useGetPropsForPlanConfirmationBS;
